import { createContext, Fragment, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon, ICON_SIZE, type IconName } from '../chrome/icons';
import { Menu, useMenu, type MenuEntry } from '../../ui/menu';
import { partsOf, type Rail, type RailButton, type RailField, type Well } from '../tools/types';
import { NumberField, Slider } from '../../ui/controls';
import { tip } from '../../ui/tooltip';
import { ColourField } from './Paint';
import { split, type DrawWith } from './ToolOptions';
import { MenuWell } from './MenuWell';
import { GUIDE_DEMOS } from '../guides';
import { Popover, useAnchor } from '../../ui/popover';

/**
 * The tool whose rail this is, so a mode's button can name its guide (`cut:knife`) for a
 * long hover. Only modes that have a guide of their own get one — a setting on the same
 * rail (a blend's spacing, a cloner's field) is not a way of working and shows none.
 */
const RailTool = createContext<string | null>(null);
function useGuideFor(): (id: string) => string | undefined {
  const tool = useContext(RailTool);
  return (id) => {
    if (!tool) return undefined;
    const key = `${tool}:${id}`;
    return GUIDE_DEMOS[key] ? key : undefined;
  };
}

/** The compact shelf for every tool: named mode menus, contextual settings and actions. */
export function ToolStrip({ rail, drawWith, tool }: { rail: Rail; drawWith: DrawWith; tool?: string }) {
  const wells = partsOf([rail.top, rail.middle, rail.bottom]);
  const points = rail.top?.kind === 'buttons' && rail.top.items.some((item) => item.id === 'object');
  if (points) return <RailTool.Provider value={tool ?? null}><PointParts rail={rail} /></RailTool.Provider>;
  // A rail is one line unless it asks for a break: an effector's amounts sit on one line
  // and how they are handed out on the next, which is two questions and reads as two.
  const lines: ReactNode[][] = [[]];
  wells.forEach((well, index) => {
    if (well.kind === 'break') lines.push([]);
    else lines[lines.length - 1]!.push(<WellPart key={index} well={well} />);
  });
  if (rail.style) lines[lines.length - 1]!.push(<DrawsWith key="style" paint={drawWith} />);
  const shown = lines.filter((line) => line.length > 0);
  if (shown.length === 0) return null;
  return (
    <RailTool.Provider value={tool ?? null}>
    <div role="toolbar" aria-label="Tool options" className="tool-options-strip glass pointer-events-auto flex max-w-[calc(100vw-32px)] flex-col items-center gap-y-[2px] rounded-[22px] px-[8px] py-[4px]">
      {shown.map((line, at) => (
        <div key={at} className="flex flex-wrap items-center justify-center gap-x-[10px] gap-y-[6px]">
          {line.map((part, index) => (
            <Fragment key={index}>
              {index > 0 ? <span aria-hidden className="h-[20px] w-px shrink-0 bg-separator" /> : null}
              {part}
            </Fragment>
          ))}
        </div>
      ))}
    </div>
    </RailTool.Provider>
  );
}

function WellPart({ well }: { well: Well }) {
  if (well.kind === 'menu') return <MenuWell well={well} placement="below" />;
  if (well.kind === 'text') return <span className="whitespace-nowrap text-[13px] font-medium text-label">{well.text}</span>;
  if (well.kind === 'fields') return <FieldsPart items={well.items} />;
  // Groups are opened out before they get here, and a break only ends a line.
  if (well.kind === 'group' || well.kind === 'break') return null;
  if (well.kind === 'slider') {
    const said = split(well.title);
    const step = well.step ?? 1;
    // As many decimals as the step has: a step of 0.05 rounded to one place stuck going
    // down and jumped by two going up, and Spread's 0.01 never moved at all. And the
    // well's own reading of the number, where it can also read typed text back — Size
    // showed -0.4 for -40% (QA 2026-09-24, cloner-F13).
    const decimals = decimalsOf(step);
    const readable = well.parse ? { format: well.format, parse: well.parse } : {};
    return (
      <div className="flex h-[36px] items-center gap-[8px]" {...tip(well.title)}>
        <span className="whitespace-nowrap text-[12px] text-label-2">{said.name}</span>
        <Slider className="w-[70px]" label={said.name} value={well.value} min={well.min} max={well.max} step={step} onChange={well.onChange} />
        <NumberField className={well.parse ? 'w-[84px]' : 'w-[58px]'} label={said.name} value={well.value} min={well.min} max={well.max} step={step} decimals={decimals} {...readable} onChange={well.onChange} />
      </div>
    );
  }
  if (well.items.some((item) => item.group)) return <GroupedPart items={well.items} />;
  if (well.items.length > 1 && well.items.every(item => item.pressed !== undefined)) return <ModePicker items={well.items} />;
  return <NamedButtons items={well.items} />;
}

/** Every mode stays in the menu; its current name stays readable on the shelf. */
function ModePicker({ items, label = 'Tool mode' }: { items: readonly RailButton[]; label?: string }) {
  const current = items.find(item => item.pressed) ?? items[0];
  if (!current) return null;
  return <RailMenu label={label} text={current.label ?? split(current.title).name} icon={current.icon} items={items} current={current.id} />;
}

function RailMenu({ label, text, icon, items, current }: { label: string; text: string; icon: IconName; items: readonly RailButton[]; current?: string }) {
  const menu = useMenu();
  const guideFor = useGuideFor();
  const entries: MenuEntry[] = [];
  let section: string | undefined;
  for (const item of items) {
    if (item.panelGroup && item.panelGroup !== section) {
      section = item.panelGroup;
      entries.push({ kind: 'header', label: section });
    }
    entries.push({ id: item.id, label: item.label ?? split(item.title).name, icon: item.icon, checked: item.pressed, disabled: item.disabled, danger: item.id === 'remove-points', guide: guideFor(item.id), onSelect: () => item.run({ more: false }) });
  }
  return <>
    <button type="button" ref={menu.ref} className="rail-command" aria-label={current ? `${label}: ${text}` : label} aria-haspopup="menu" aria-expanded={menu.open} onClick={menu.toggle}>
      <Icon name={icon} size={16} /><span>{text}</span><Icon name="chevron" size={ICON_SIZE.indicator} />
    </button>
    <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} placement="below" label={label} current={current} items={entries} />
  </>;
}

function RailAction({ item, prominent = false, iconOnly = false }: { item: RailButton; prominent?: boolean; iconOnly?: boolean }) {
  const guideFor = useGuideFor();
  const preview = useRef(item.preview);
  preview.current = item.preview;
  const previewState = useRef({ hovered: false, focused: false });
  const showPreview = (source: 'hovered' | 'focused', shown: boolean) => {
    previewState.current[source] = shown;
    preview.current?.(!item.disabled && (previewState.current.hovered || previewState.current.focused));
  };
  useEffect(() => () => preview.current?.(false), []);
  useEffect(() => { if (item.disabled) preview.current?.(false); }, [item.disabled]);
  const label = item.label ?? split(item.title).name;
  const help = iconOnly && !item.title.startsWith(label) ? `${label} — ${item.title}` : item.title;
  return <button type="button" className={`rail-command${prominent ? ' rail-done' : ''}${iconOnly ? ' rail-icon' : ''}`} aria-label={label} disabled={item.disabled} aria-pressed={item.pressed} data-guide={guideFor(item.id)} {...tip(help)} onPointerEnter={() => showPreview('hovered', true)} onPointerLeave={() => showPreview('hovered', false)} onFocus={() => showPreview('focused', true)} onBlur={() => showPreview('focused', false)} onClick={event => item.run({ more: event.metaKey || event.ctrlKey })}>
    {!item.bare && <Icon name={item.icon} size={16} />}{!iconOnly && <span>{label}</span>}
  </button>;
}

/** How many decimal places a step needs: 1 → 0, 0.1 → 1, 0.05 and 0.01 → 2. */
function decimalsOf(step: number): number {
  for (let places = 0; places < 6; places++) {
    const scaled = step * 10 ** places;
    if (Math.abs(scaled - Math.round(scaled)) < 1e-9) return places;
  }
  return 6;
}

/**
 * A well holding several choices and switches side by side: one named mode menu per
 * group, in the order the groups first appear, and each button with no group as a mark
 * of its own that shows whether it is on (cloner-F5).
 */
function GroupedPart({ items }: { items: readonly RailButton[] }) {
  const seen = new Set<string>();
  return <div className="flex items-center gap-[6px]">{items.map(item => {
    if (!item.group) return <RailAction key={item.id} item={item} />;
    if (seen.has(item.group)) return null;
    seen.add(item.group);
    return <ModePicker key={item.group} label={item.group} items={items.filter(one => one.group === item.group)} />;
  })}</div>;
}

function NamedButtons({ items }: { items: readonly RailButton[] }) {
  return <div className="flex items-center gap-[3px]">{items.map(item => <RailAction key={item.id} item={item} />)}</div>;
}

/** Quantities that are all in play together, each with its name in front of its number. */
function FieldsPart({ items }: { items: readonly RailField[] }) {
  return (
    <div className="flex items-center gap-[6px]">
      {items.map((field) => {
        const readable = field.parse ? { format: field.format, parse: field.parse } : {};
        return (
          <span key={field.id} className="flex" {...tip(field.title)}>
            <NumberField
              className={field.parse ? 'w-[124px]' : 'w-[104px]'}
              prefix={field.label}
              prefixWidth={field.label.length > 5 ? 52 : 42}
              label={field.label}
              value={field.value}
              min={field.min}
              max={field.max}
              step={field.step ?? 1}
              decimals={decimalsOf(field.step ?? 1)}
              {...readable}
              onChange={field.onChange}
            />
          </span>
        );
      })}
    </div>
  );
}

/** Point editing is a focused workspace with direct geometry actions and named selection helpers. */
function PointParts({ rail }: { rail: Rail }) {
  const modes = rail.top?.kind === 'buttons' ? rail.top.items : [];
  const done = modes.find(item => item.id === 'object');
  const helpers = rail.bottom?.kind === 'buttons' ? rail.bottom.items : [];
  const edits = rail.middle?.kind === 'buttons' ? rail.middle.items : [];
  const add = edits.find(item => item.id === 'add-point');
  const connection = edits.find(item => item.id === 'close-path');
  const remove = edits.find(item => item.id === 'remove-points');
  // The three ways of picking points — Direct, Box, Lasso — live here and nowhere else:
  // they last as long as a path's points are open (the invariant). The dock's own
  // picker, which showed them all day, went with the uplift (15 Oct).
  const pointing = modes.filter(item => item.id !== 'object');
  return <div role="toolbar" aria-label="Point editing" className="point-options-strip">
      <div role="radiogroup" aria-label="Point selection mode" className="point-action-group point-mode-group">
        {pointing.map(item => <RailAction key={item.id} item={{ ...item, label: item.id === 'area' ? 'Box' : split(item.title).name }} />)}
      </div>
      <div className="point-action-group">
        <PointSelectionMenu rail={rail} items={helpers} />
      </div>
      <div role="group" aria-label="Point type" className="point-action-group point-kind-group">
        {edits.filter(item => item.id === 'smooth' || item.id === 'corner').map(item => <RailAction key={item.id} item={item} />)}
      </div>
      <div role="group" aria-label="Path actions" className="point-action-group">
        {add && <RailAction item={add} />}
        {connection && <RailAction item={{ ...connection, bare: true }} />}
      </div>
      <div role="group" aria-label="Selected point actions" className="point-action-group">
        <PointPosition fields={rail.pointEditing?.coordinates} />
        {remove && <RailAction item={{ ...remove, bare: true, label: 'Delete', title: 'Delete selected points — ⌫' }} />}
        {done && <RailAction prominent item={{ ...done, label: 'Done', title: 'Done — return to object selection · Enter or Esc', icon: 'check', pressed: undefined }} />}
      </div>
  </div>;
}

function PointSelectionMenu({ rail, items }: { rail: Rail; items: readonly RailButton[] }) {
  const menu = useMenu();
  const context = rail.pointEditing;
  const count = context?.selected ?? 0;
  const notes: Record<string, string> = {
    grow: 'Include the neighboring points', shrink: 'Remove the ends of the selection',
    invert: 'Select the other points on this path', mirror: 'Include matching points across the symmetry axis',
  };
  const ordered = ['all-points', 'deselect-points', 'grow', 'shrink', 'invert', 'mirror'].flatMap(id => items.filter(item => item.id === id));
  const entries: MenuEntry[] = [
    { kind: 'header', label: `${count} of ${context?.total ?? 0} points selected` },
    ...ordered.flatMap((item): MenuEntry[] => [
      ...(['grow', 'mirror'].includes(item.id) ? [{ kind: 'separator' as const }] : []),
      { id: item.id, label: item.label ?? split(item.title).name, icon: item.icon, disabled: item.disabled,
        checked: item.pressed, note: item.disabled && item.id === 'mirror' ? 'No symmetry axis found for this path' : notes[item.id],
        shortcut: item.id === 'all-points' ? '⌘A' : undefined, stay: true, onSelect: () => item.run({ more: false }) },
    ]),
  ];
  return <>
    <button type="button" ref={menu.ref} className="rail-command point-selection-menu" aria-label={`Point selection: ${count} selected`} aria-haspopup="menu" aria-expanded={menu.open} {...tip('Select points · Shift-click to select more')} onClick={menu.toggle}>
      <span>{count ? `${count} ${count === 1 ? 'point' : 'points'}` : 'Select points'}</span><Icon name="chevron" size={ICON_SIZE.indicator} />
    </button>
    <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} items={entries} placement="below" label="Point selection" width={270} />
  </>;
}

function PointPosition({ fields }: { fields?: readonly RailField[] }) {
  const [anchor, ref] = useAnchor<HTMLButtonElement>();
  const [open, setOpen] = useState(false);
  useEffect(() => { if (!fields) setOpen(false); }, [fields]);
  return <>
    <button type="button" ref={ref} className="rail-command" aria-label="Point position" aria-haspopup="dialog" aria-expanded={open} disabled={!fields} {...tip(fields ? 'Edit point coordinates' : 'Select one point to edit its coordinates')} onClick={() => setOpen(was => !was)}>Position</button>
    <Popover open={open && !!fields} anchor={anchor} onClose={() => setOpen(false)} placement="below" align="center" width={260} label="Point position" className="point-position-popover">
      <div className="point-position-heading"><strong>Point position</strong><button type="button" aria-label="Close point position" onClick={() => setOpen(false)}><Icon name="close" size={ICON_SIZE.small} /></button></div>
      <p>Coordinates on the canvas</p>
      <div className="point-position-fields">{fields?.map(field => <NumberField key={field.id} label={`Point ${field.label}`} prefix={field.label} value={field.value} min={field.min} max={field.max} step={field.step} decimals={2} format={field.format} parse={field.parse} onChange={field.onChange} />)}</div>
      <p>Arrow keys nudge by 1. Hold Shift for 10.</p>
    </Popover>
  </>;
}

/** What the tool will draw with: fill, outline and its weight. */
function DrawsWith({ paint }: { paint: DrawWith }) {
  return (
    <div className="flex h-[36px] items-center gap-[6px]">
      <ColourField size="small" hex={paint.fillOn ? paint.fill : null} ink={paint.fillInk} label="Fill to draw with" title="Fill" onChange={paint.onFill} onNone={paint.onFillNone} placement="below" />
      <ColourField size="small" hex={paint.weight > 0 ? paint.stroke : null} ink={paint.strokeInk} label="Stroke to draw with" title="Stroke" onChange={paint.onStroke} onNone={paint.onStrokeNone} placement="below" />
      <NumberField className="w-[58px]" label="Stroke weight to draw with" value={paint.weight} min={0} max={200} step={0.5} decimals={1} onChange={paint.onWeight} />
    </div>
  );
}
